/**
 * Single-source fetch wrapper. Two key behaviors:
 *
 *   1. `credentials: "include"` so the .NET JWT cookie rides along —
 *      the backend reads `access_token` from `Request.Cookies` (see
 *      JwtBearerEvents.OnMessageReceived in Program.cs).
 *   2. Treats 4xx/5xx as throws so callers (and TanStack Query) get
 *      uniform error-handling. The thrown <ApiError> carries the
 *      parsed JSON body so UI code can read `.code` / `.errors`.
 *
 * Path conventions in this codebase:
 *   `/auth/*`  — legacy ViiLearn-style auth controller
 *   `/api/*`   — Phase 4+ HosterDash controllers
 *
 * Origin resolution:
 *   - If `VITE_API_ORIGIN` is set (.env.development), every relative path
 *     is prefixed with it — the browser calls the backend DIRECTLY,
 *     bypassing the Vite proxy. CORS + `SameSite=None; Secure` cookies
 *     must be configured server-side for this to work.
 *   - If unset, paths stay relative and Vite's dev proxy forwards them.
 *   - Absolute URLs (anything starting with http) pass through unchanged.
 */

const API_BASE = (import.meta.env.VITE_API_ORIGIN as string | undefined) ?? "";

function resolveUrl(path: string): string {
  if (/^https?:\/\//i.test(path)) return path;
  if (!API_BASE) return path;
  return API_BASE.replace(/\/+$/, "") + (path.startsWith("/") ? path : `/${path}`);
}


export class ApiError extends Error {
  status: number;
  body: unknown;
  code?: string;

  constructor(status: number, body: unknown, message: string) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.body = body;
    if (body && typeof body === "object" && "code" in body) {
      this.code = String((body as { code: unknown }).code);
    }
  }
}

interface RequestOptions {
  method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  body?: unknown;
  signal?: AbortSignal;
  headers?: Record<string, string>;
}

/**
 * Silent access-token refresh. The access_token cookie is short-lived; when a
 * call comes back 401 we POST /auth/refresh once (which rotates the cookies
 * using the long-lived refresh_token) and retry the original request. A single
 * shared in-flight promise prevents a refresh stampede when many requests 401
 * at once.
 */
let refreshInFlight: Promise<boolean> | null = null;

function isAuthFlowPath(path: string): boolean {
  return path.includes("/auth/refresh") ||
         path.includes("/auth/login") ||
         path.includes("/auth/logout") ||
         // A 401 here means the temp_token (2FA session) is missing/expired/
         // wrong-code — not that the regular access token needs refreshing.
         // Retrying via /auth/refresh would just fail the same way again.
         path.includes("/auth/2fa/setup") ||
         path.includes("/auth/2fa/verify");
}

export function refreshAccessToken(): Promise<boolean> {
  if (!refreshInFlight) {
    refreshInFlight = fetch(resolveUrl("/auth/refresh"), {
      method: "POST",
      credentials: "include",
      headers: { Accept: "application/json" }
    })
      .then((r) => r.ok)
      .catch(() => false)
      .finally(() => {
        // Release the lock on the next tick so concurrent callers share this
        // result but a later 401 can trigger a fresh refresh.
        setTimeout(() => { refreshInFlight = null; }, 0);
      });
  }
  return refreshInFlight;
}

async function request<T>(path: string, opts: RequestOptions = {}, isRetry = false): Promise<T> {
  const headers: Record<string, string> = {
    Accept: "application/json",
    ...opts.headers
  };

  const init: RequestInit = {
    method: opts.method ?? "GET",
    credentials: "include",
    headers,
    signal: opts.signal
  };

  if (opts.body !== undefined) {
    headers["Content-Type"] = "application/json";
    init.body = JSON.stringify(opts.body);
  }

  const res = await fetch(resolveUrl(path), init);

  // Reactive refresh: a 401 likely means the access token expired. Try once to
  // refresh + replay, except on the auth endpoints themselves (avoids loops).
  if (res.status === 401 && !isRetry && !isAuthFlowPath(path)) {
    const refreshed = await refreshAccessToken();
    if (refreshed) return request<T>(path, opts, true);
  }

  // 204 No Content → undefined; otherwise parse JSON (be lenient on
  // empty bodies in case the controller forgot to set the header).
  let parsed: unknown = undefined;
  if (res.status !== 204) {
    const text = await res.text();
    if (text) {
      try { parsed = JSON.parse(text); }
      catch { parsed = text; }
    }
  }

  if (!res.ok) {
    const message =
      (parsed && typeof parsed === "object" && "message" in parsed
        ? String((parsed as { message: unknown }).message)
        : null) ??
      (parsed && typeof parsed === "object" && "error" in parsed
        ? String((parsed as { error: unknown }).error)
        : null) ??
      `HTTP ${res.status}`;
    throw new ApiError(res.status, parsed, message);
  }

  return parsed as T;
}

export const api = {
  get:    <T>(path: string, signal?: AbortSignal)            => request<T>(path, { signal }),
  post:   <T>(path: string, body?: unknown, signal?: AbortSignal) => request<T>(path, { method: "POST",   body, signal }),
  patch:  <T>(path: string, body?: unknown, signal?: AbortSignal) => request<T>(path, { method: "PATCH",  body, signal }),
  put:    <T>(path: string, body?: unknown, signal?: AbortSignal) => request<T>(path, { method: "PUT",    body, signal }),
  delete: <T>(path: string, signal?: AbortSignal)            => request<T>(path, { method: "DELETE", signal })
};
